import { FC } from 'react';

import { useMutation } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';

import {
    Box,
    Loading,
    LoadingButton,
    Stack,
    Typography,
} from '@thirdspacelearning/library';

import { initiateSelfLaunchedSession } from '@api';
import { useAppDispatch } from '@store';
import { loadAppData } from '@store/initialisation';
import { studyBuddySvg } from '@assets/dashboard/los-panel';

import { SubjectDirectory } from '@types';

const sx = {
    wrapper: {
        flexDirection: { xs: 'column', md: 'row' },
        justifyContent: 'space-between',
        alignItems: { xs: 'flex-start', md: 'center' },
        width: '100%',
        px: 4,
        py: 2,
        gap: 2,
    },
    actions: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 2,
        flexShrink: 0,
    },
    studyBuddy: {
        display: { xs: 'none', md: 'block' },
    },
};

interface Props {
    lastStartedLO: SubjectDirectory;
}

const SessionResume: FC<Props> = ({ lastStartedLO }) => {
    const dispatch = useAppDispatch();

    const subjectId = lastStartedLO.subject_id;

    const { mutate: resumeSession, isPending } = useMutation({
        mutationFn: () =>
            initiateSelfLaunchedSession(subjectId as number, true),
        onSuccess: () => {
            dispatch(loadAppData());
        },
        onError: (error: Error) => {
            enqueueSnackbar('Please try again or refresh the page', {
                title: error.message,
                testId: 'resume-session-error',
                variant: 'sxSnackbar',
                severity: 'error',
            });
        },
    });

    return (
        <Stack sx={sx.wrapper}>
            <Stack>
                <Typography
                    variant="h5"
                    fontWeight="bold"
                    data-testid="session-resume-title"
                >
                    Resume from where you left off
                </Typography>
                <Typography
                    variant="subtitle2"
                    data-testid="session-resume-lo-title"
                >
                    You will resume "{lastStartedLO.subject_title}"
                </Typography>
            </Stack>
            <Stack sx={sx.actions}>
                <LoadingButton
                    variant="contained"
                    loading={isPending}
                    loadingIndicator={<Loading size={24} />}
                    onClick={() => resumeSession()}
                    data-testid="session-resume-btn"
                >
                    Resume Lesson
                </LoadingButton>
                <Box
                    component="img"
                    alt=""
                    src={studyBuddySvg}
                    sx={sx.studyBuddy}
                />
            </Stack>
        </Stack>
    );
};

export default SessionResume;
